import {useEffect, useState, useSyncExternalStore, type CSSProperties} from 'react';

export interface DockAnchor {
    readonly horizontal: 'left' | 'right';
    readonly x: number;
    readonly vertical: 'top' | 'bottom';
    readonly y: number;
}

export interface DockRect {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
}

export interface Viewport {
    readonly width: number;
    readonly height: number;
}

export const DEFAULT_DOCK: DockAnchor = {horizontal: 'right', x: 24, vertical: 'bottom', y: 24};
export const DOCK_MARGIN = 8;

export const cornerDock = (horizontal: DockAnchor['horizontal'], vertical: DockAnchor['vertical']): DockAnchor =>
    ({horizontal, x: DEFAULT_DOCK.x, vertical, y: DEFAULT_DOCK.y});
const STORAGE_KEY = 'phq.callDock.v1';

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max));

const isAnchor = (value: unknown): value is DockAnchor => {
    const anchor = value as Partial<DockAnchor> | null;
    return !!anchor
        && (anchor.horizontal === 'left' || anchor.horizontal === 'right')
        && (anchor.vertical === 'top' || anchor.vertical === 'bottom')
        && Number.isFinite(anchor.x)
        && Number.isFinite(anchor.y);
};

const readStored = (): DockAnchor => {
    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null');
        return isAnchor(parsed) ? parsed : DEFAULT_DOCK;
    } catch {
        return DEFAULT_DOCK;
    }
};

const persist = (anchor: DockAnchor): boolean => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
        return true;
    } catch {
        return false;
    }
};

let current: DockAnchor | undefined;
const listeners = new Set<() => void>();

export const callDock = {
    get(): DockAnchor {
        current ??= readStored();
        return current;
    },
    set(anchor: DockAnchor): void {
        current = anchor;
        persist(anchor);
        listeners.forEach(listener => listener());
    },
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export const anchorFromRect = (rect: DockRect, viewport: Viewport): DockAnchor => {
    const horizontal = rect.left + rect.width / 2 < viewport.width / 2 ? 'left' : 'right';
    const vertical = rect.top + rect.height / 2 < viewport.height / 2 ? 'top' : 'bottom';
    const x = horizontal === 'left' ? rect.left : viewport.width - rect.left - rect.width;
    const y = vertical === 'top' ? rect.top : viewport.height - rect.top - rect.height;
    return {
        horizontal,
        x: Math.round(clamp(x, DOCK_MARGIN, viewport.width - rect.width - DOCK_MARGIN)),
        vertical,
        y: Math.round(clamp(y, DOCK_MARGIN, viewport.height - rect.height - DOCK_MARGIN)),
    };
};

export const dockPosition = (anchor: DockAnchor, size: {readonly width: number; readonly height?: number}, viewport: Viewport): CSSProperties => {
    const x = clamp(anchor.x, DOCK_MARGIN, viewport.width - size.width - DOCK_MARGIN);
    const y = clamp(anchor.y, DOCK_MARGIN, viewport.height - (size.height ?? 0) - DOCK_MARGIN);
    return {[anchor.horizontal]: x, [anchor.vertical]: y, maxHeight: `calc(100vh - ${y + DOCK_MARGIN * 3}px)`};
};

const readViewport = (): Viewport => ({width: window.innerWidth, height: window.innerHeight});

export const useViewport = (): Viewport => {
    const [viewport, setViewport] = useState(readViewport);
    useEffect(() => {
        const onResize = () => setViewport(readViewport());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return viewport;
};

export const useCallDock = (): DockAnchor => useSyncExternalStore(callDock.subscribe, callDock.get, () => DEFAULT_DOCK);

export const useDockStyle = (size: {readonly width: number; readonly height?: number}): CSSProperties => {
    const anchor = useCallDock();
    const viewport = useViewport();
    return dockPosition(anchor, size, viewport);
};
